/** Same cutoff as density optimization: above this, SVG circle join stalls. */
export const SIMPLE_SCATTER_UPLOT_MIN_POINTS = 5000;

export interface ScatterUPlotSeriesPoint {
  x: number;
  y: number;
  visible?: boolean;
}

export interface PreparedSimpleScatterUPlotSeries {
  xs: number[];
  ys: number[];
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

function paddedRange(min: number, max: number, padLo: number, padHi: number): [number, number] {
  const span = max - min || 1;
  return [min - span * padLo, max + span * padHi];
}

/**
 * uPlot assumes monotonic X. Scatter X is not time-ordered, so we sort a copy for
 * setData and return explicit linear extents from the full cloud (not i0..i1).
 */
export function prepareSimpleScatterUPlotSeries(
  points: ScatterUPlotSeriesPoint[]
): PreparedSimpleScatterUPlotSeries {
  const scalePoints = points.filter((p) => p.visible !== false);
  const used = scalePoints.length > 0 ? scalePoints : points;
  if (used.length === 0) {
    return { xs: [0, 1], ys: [0, 1], xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
  }
  const sorted = used.slice().sort((a, b) => a.x - b.x);
  const xs = new Array<number>(sorted.length);
  const ys = new Array<number>(sorted.length);
  let xMin = sorted[0]!.x;
  let xMax = sorted[0]!.x;
  let yMin = sorted[0]!.y;
  let yMax = sorted[0]!.y;
  for (let i = 0; i < sorted.length; i++) {
    const p = sorted[i]!;
    xs[i] = p.x;
    ys[i] = p.y;
    if (p.x < xMin) {xMin = p.x;}
    if (p.x > xMax) {xMax = p.x;}
    if (p.y < yMin) {yMin = p.y;}
    if (p.y > yMax) {yMax = p.y;}
  }
  const [x0, x1] = paddedRange(xMin, xMax, 0.02, 0.02);
  const [y0, y1] = paddedRange(yMin, yMax, 0.01, 0.10);
  return { xs, ys, xMin: x0, xMax: x1, yMin: y0, yMax: y1 };
}

export function shouldUseSimpleScatterUPlot(
  useUPlotProp: boolean | undefined,
  pointCount: number
): boolean {
  if (useUPlotProp === false) {
    return false;
  }
  if (useUPlotProp === true) {
    return true;
  }
  return pointCount >= SIMPLE_SCATTER_UPLOT_MIN_POINTS;
}
